import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
import { IconSizes } from '@/constants/iconTypes';
import { RouteFn } from '@/hooks/use-flow';
import { useCoverageSection, useCoverageValidation } from '@/hooks/use-quote';
import { useDynamicComponent } from '@/hooks/use-dynamic-component';
import { Component, Modal, ModalLink } from '@/schemas/sceneDataSchema';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { DynamicRenderer } from './DynamicRenderer';
import { CoverageSectionSelection } from './CoverageSectionSelection';
import { InertIcon } from './InertIcon';

interface CoverageSectionProps {
  symbol: string;
  apply_to: string;
  title: string;
  description: string;
  icon: string;
  attribute_components: Component[];
  route: RouteFn;
  modal_link: ModalLink | null;
  modal: Modal | null;
  confirmation_modal_id?: string | null;
}

export const CoverageSection: React.FC<CoverageSectionProps> = ({
  symbol,
  apply_to,
  title,
  description,
  icon,
  attribute_components,
  modal_link,
  modal,
  confirmation_modal_id,
  route,
}) => {
  const { isRequired, isDeclined, handleDeclinedToggle } = useCoverageSection({ symbol, scope: apply_to });
  const coverageValidation = useCoverageValidation({ symbol, scope: apply_to });
  const { isReadonlyComponentType } = useDynamicComponent();
  const readonly = attribute_components.some((comp) => isReadonlyComponentType(comp.type));
  const showAlert = coverageValidation && !coverageValidation.valid && coverageValidation.message;

  return (
    <Stack spacing={3}>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        gap={2}
      >
        <Box
          display='flex'
          alignItems='center'
          gap={2}
        >
          <InertIcon
            icon_name={icon}
            size={IconSizes.SMALL}
          />
          <Typography
            variant='h4'
            component='h3'
            fontWeight='medium'
            sx={{ lineHeight: 1.15 }}
          >
            {title}
          </Typography>
        </Box>
        {
          /* Disable toggle if readonly components are present */
          !readonly && (
            <Box
              display='flex'
              alignItems='center'
              gap={2}
            >
              <CoverageSectionSelection
                isDeclined={isDeclined}
                isRequired={isRequired}
                onToggle={handleDeclinedToggle}
                scope={apply_to}
                symbol={symbol}
                confirmationModalId={confirmation_modal_id}
              />
            </Box>
          )
        }
      </Box>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-between'
        gap={2}
      >
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{ fontSize: '1rem' }}
        >
          {description}
          {modal_link && (
            <DynamicRenderer
              components={[modal_link]}
              route={route}
            />
          )}
        </Typography>
      </Box>

      {showAlert && (
        <Alert
          severity={AlertSeverity.ERROR}
          title={null}
          titleGutterBottom={null}
          text={coverageValidation.message}
        />
      )}

      {!isDeclined && attribute_components && attribute_components.length > 0 && (
        <DynamicRenderer
          components={attribute_components}
          route={route}
        />
      )}
      {modal && (
        <DynamicRenderer
          components={[modal]}
          route={route}
        />
      )}
    </Stack>
  );
};
